import { computed, getCurrentInstance } from 'vue';
import dayjs from 'dayjs';
import { convertNumberToStr } from '@/utils/api';
import i18n, { EN, ZH } from '@/locales';
import { convertPX } from '@/utils/echarts';

/**
 * K线图通用逻辑封装
 * @param {Object} props - 组件的 props
 * @returns {Object} - 返回共享的计算属性和工具函数
 */
export function useKChartCommon(props) {
	const { proxy } = getCurrentInstance();

	// 股票类型计算属性
	const isHKStock = computed(() => {
		if (!props.fullCode) return false;
		return props.fullCode.toUpperCase().includes('HK');
	});

	const isUSStock = computed(() => {
		if (!props.fullCode) return false;
		return props.fullCode.toUpperCase().includes('US');
	});

	// 涨跌颜色
	const upColor = i18n.locale === ZH ? '#fa4646' : '#30bf54';
	const downColor = i18n.locale === ZH ? '#30bf54' : '#fa4646';

	/**
	 * 格式化 tooltip 字符串
	 * @param {string} txt 文案
	 * @param {string} value 值
	 * @param {number} comParePrice 比较的值，判断颜色
	 * @param {number} prePrice 前一天收盘价
	 */
	function tooltipStr(txt, value, comParePrice, prePrice) {
		const color = comParePrice >= prePrice ? upColor : downColor;
		return `${txt}：<span style="color:${color}">${value}</span><br/>`;
	}

	/**
	 * 分割数据为 ECharts 所需格式
	 * @param {Array} rawData 原始数据
	 * @returns {Object} - { categoryData, values, volumes }
	 */
	function splitData(rawData) {
		let categoryData = []; // [时间]
		let values = []; // [开盘价，收盘价，最低价，最高价]
		let volumes = []; // [索引，成交量，1涨-1跌]

		for (let i = 0; i < rawData.length; i++) {
			const {
				tradingDay,
				openPrice,
				closePrice,
				highPrice,
				lowPrice,
				changePct,
				changeQuota,
				turnoverValue,
				turnoverVolume,
				turnoverVolumeShares
			} = rawData[i];

			categoryData.push(dayjs(tradingDay).format('YYYY/MM/DD'));
			values.push([
				openPrice,
				closePrice,
				lowPrice,
				highPrice,
				changePct,
				changeQuota,
				isHKStock.value || isUSStock.value
					? turnoverVolumeShares
					: i18n.locale === EN
					? turnoverVolume * 100
					: turnoverVolume,
				turnoverValue
			]);
			volumes.push([
				i,
				isHKStock.value || isUSStock.value
					? turnoverVolumeShares
					: i18n.locale === EN
					? turnoverVolume * 100
					: turnoverVolume,
				openPrice > closePrice ? 1 : -1
			]);
		}

		return {
			categoryData: categoryData,
			values: values,
			volumes: volumes
		};
	}

	/**
	 * 计算移动平均线
	 * @param {number} dayCount MA天数
	 * @param {Object} data 数据对象
	 * @returns {Array} MA数据数组
	 */
	function calculateMA(dayCount, data) {
		var result = [];
		for (var i = 0, len = data.values.length; i < len; i++) {
			if (i < dayCount) {
				result.push('-');
				continue;
			}
			var sum = 0;
			for (var j = 0; j < dayCount; j++) {
				sum += data.values[i - j][1];
			}
			result.push(+(sum / dayCount).toFixed(3));
		}
		return result;
	}

	/**
	 * 正数补全+号
	 * @param {number} num 数字
	 * @returns {string|number} 带符号的数字
	 */
	function addPlus(num) {
		return num > 0 ? '+' + num : num;
	}

	/**
	 * 格式化英文单位
	 * @param {number} amount 金额
	 * @returns {string} 格式化后的字符串
	 */
	function formatEnUnit(amount) {
		return amount >= 1e9
			? (amount / 1e9).toFixed(2) + 'B'
			: amount >= 1e6
			? (amount / 1e6).toFixed(2) + 'M'
			: amount >= 1e3
			? (amount / 1e3).toFixed(2) + 'K'
			: amount.toFixed(2);
	}

	/**
	 * 格式化成交量单位（中文环境）
	 * @param {number} value 成交量值
	 * @returns {string} 格式化后的字符串
	 */
	function formatVolumeUnit(value) {
		return convertNumberToStr(value) + `${isHKStock.value ? '股' : '手'}`;
	}

	/**
	 * 创建 tooltip position 函数
	 * @returns {Function} position 函数
	 */
	function createTooltipPosition() {
		return function (pos, params, el, elRect, size) {
			const obj = {
				top: convertPX(10)
			};
			obj[['left', 'right'][+(pos[0] < size.viewSize[0] / 2)]] = convertPX(30);
			return obj;
		};
	}

	/**
	 * 创建 tooltip formatter 函数
	 * @param {Object} data splitData 返回的数据对象
	 * @returns {Function} formatter 函数
	 */
	function createTooltipFormatter(data) {
		return function (params) {
			let res = `<div style="color:#999;line-height:1.7;font-size:${convertPX(12)}px"><span style="color:#333">${
				params[0].name
			}</span> <br/>`;

			for (let i = 0, l = params.length; i < l; i++) {
				// 获取前一天收盘价
				const preCloseIndex = params[i].dataIndex > 0 ? params[i].dataIndex - 1 : 0;
				const preClose = data.values[preCloseIndex][1];

				let { seriesName, value, seriesType } = params[i];

				if (seriesName === 'Volume') {
					res += '';
				} else if (seriesType === 'candlestick') {
					res += tooltipStr(proxy.$t('enterprise.k-opening'), value[1]?.toFixed(2), value[1], preClose);
					res += tooltipStr(proxy.$t('enterprise.k-high'), value[4]?.toFixed(2), value[4], preClose);
					res += tooltipStr(proxy.$t('enterprise.k-low'), value[3]?.toFixed(2), value[3], preClose);
					res += tooltipStr(proxy.$t('enterprise.k-closing'), value[2]?.toFixed(2), value[2], preClose);
					res += tooltipStr(
						proxy.$t('enterprise.%Chg'),
						`${addPlus(value[5]?.toFixed(2))}%`,
						value[2],
						preClose
					);
					res += tooltipStr(proxy.$t('enterprise.chg'), value[6]?.toFixed(2), value[2], preClose);
					res +=
						`${proxy.$t('enterprise.k-volume')}：` +
						convertNumberToStr(value[7]) +
						`${i18n.locale === EN ? '' : isHKStock.value ? '股' : '手'}<br/>`;
					res += `${proxy.$t('enterprise.k-turnover')}：` + convertNumberToStr(value[8]) + '<br/>';
				} else {
					const val = typeof value === 'number' ? value?.toFixed(2) : '-';
					res += seriesName + '：' + val + '<br/>';
				}
			}
			return res + '</div>';
		};
	}

	return {
		isHKStock,
		isUSStock,

		upColor,
		downColor,

		tooltipStr,
		splitData,
		calculateMA,
		addPlus,
		formatEnUnit,
		formatVolumeUnit,

		createTooltipPosition,
		createTooltipFormatter
	};
}
